// Footer Install PWA Button - Elegant and compact
import { useState, useEffect } from "react";
import { Download, Smartphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const installTranslations = {
  ru: {
    install: "Установить приложение",
    install_short: "Установить",
    installed: "Установлено",
    ios_hint: "Нажмите «Поделиться» → «На экран Домой»"
  },
  en: {
    install: "Install App",
    install_short: "Install",
    installed: "Installed",
    ios_hint: "Tap Share → Add to Home Screen"
  },
  es: {
    install: "Instalar App",
    install_short: "Instalar",
    installed: "Instalada",
    ios_hint: "Toca Compartir → Añadir a inicio"
  },
  fr: {
    install: "Installer l'App",
    install_short: "Installer",
    installed: "Installée",
    ios_hint: "Appuyez sur Partager → Sur l'écran d'accueil"
  },
  de: {
    install: "App installieren",
    install_short: "Installieren",
    installed: "Installiert",
    ios_hint: "Tippe auf Teilen → Zum Home-Bildschirm"
  },
  cs: {
    install: "Instalovat aplikaci",
    install_short: "Instalovat",
    installed: "Nainstalováno",
    ios_hint: "Klepněte na Sdílet → Na plochu"
  },
  sk: {
    install: "Inštalovať aplikáciu",
    install_short: "Inštalovať",
    installed: "Nainštalované",
    ios_hint: "Klepnite na Zdieľať → Na plochu"
  },
  uk: {
    install: "Встановити додаток",
    install_short: "Встановити",
    installed: "Встановлено",
    ios_hint: "Натисніть «Поділитися» → «На екран Домому»"
  }
};

const FooterInstallButton = ({ language = "en" }) => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(() => {
    // Initialize iOS check synchronously
    if (typeof window !== 'undefined') {
      return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    }
    return false;
  });
  const [showIOSHint, setShowIOSHint] = useState(false);
  
  const t = installTranslations[language] || installTranslations.en;

  // Check if already installed (standalone mode) - outside useEffect
  const isInstalled = typeof window !== 'undefined' && window.matchMedia("(display-mode: standalone)").matches;

  useEffect(() => {
    if (isInstalled) return;

    // Listen for install prompt
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstall);
    
    // Listen for successful install
    const handleAppInstalled = () => {
      setDeferredPrompt(null);
    };
    window.addEventListener("appinstalled", handleAppInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstall);
      window.removeEventListener("appinstalled", handleAppInstalled);
    };
  }, [isInstalled]);

  const handleInstall = async () => {
    if (isIOS) {
      setShowIOSHint(!showIOSHint);
      return;
    }

    if (!deferredPrompt) {
      // Show instructions for Android if no prompt available
      const hints = {
        ru: "Откройте меню браузера (⋮) → Установить приложение",
        uk: "Відкрийте меню браузера (⋮) → Встановити додаток",
        en: "Open browser menu (⋮) → Install app"
      };
      toast.info(hints[language] || hints.en);
      return;
    }

    deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    setDeferredPrompt(null);
  };

  // Don't show if already installed
  if (isInstalled) {
    return null;
  }

  // Check if mobile device
  const isMobile = typeof window !== 'undefined' && /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

  // Show on mobile always, on desktop only when prompt available
  if (!isMobile && !deferredPrompt) {
    return null;
  }

  return (
    <div className="relative">
      <Button
        variant="outline"
        size="sm"
        onClick={handleInstall}
        className="gap-2 text-xs sm:text-sm border-accent/50 hover:bg-accent/10 hover:border-accent"
        data-testid="footer-install-btn"
      >
        <Smartphone className="w-4 h-4" />
        <span className="hidden sm:inline">{t.install}</span>
        <span className="sm:hidden">{t.install_short}</span>
      </Button>
      
      {/* iOS hint tooltip */}
      {isIOS && showIOSHint && (
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 p-2 bg-popover border rounded-lg shadow-lg text-xs text-center z-50 animate-in fade-in slide-in-from-bottom-2">
          <p>{t.ios_hint}</p>
          <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-popover border-r border-b rotate-45"></div>
        </div>
      )}
    </div>
  );
};

export default FooterInstallButton;
